import { CalendarClock, Loader2, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/atoms/dialog';
import SignaturePad from '@/components/molecules/SignaturePad';
import { CustomerTermsBody } from '@/components/public/CustomerTermsBody';
import { singleScopeThemeVars } from '@/lib/quote/singleScopeTheme';
import { printableSingleScopeCompany } from '@/lib/quote/singleScopeCompany';
import { membershipOfferAdjustmentForLines } from '@/lib/sales/membership-offer';
import { expiredQuoteMessage } from '@/lib/quote/expiredMessage';
import { PaymentPlanSlot } from './PaymentPlanSlot';
import { fmt, fmtDateOnly, useQuoteActions } from '../quoteActionContext';

/** One approval dialog for estimates and proposal checkout, including consent and signatures. */
export function QuoteApproveDialog() {
  const {
    estimate,
    documentCompany,
    documentLabel,
    documentNumber,
    v,
    resolvedDocLayout,
    presentation,
    approveModalOpen,
    setApproveModalOpen,
    requiredSubtotal,
    selectedOptionalRows,
    acceptMembershipOffer,
    membershipOffer,
    displayMembershipSavings,
    membershipSavingsPlanNames,
    membershipPlanPrice,
    membershipSetupFee,
    selectedDiscountTotal,
    estimateDiscountLabel,
    approvalTaxTotal,
    approvalGrandTotal,
    approvalMethod,
    sigPadRef,
    sigName,
    setSigName,
    sigHasInk,
    setSigHasInk,
    sigNote,
    setSigNote,
    termsAccepted,
    setTermsAccepted,
    hasTerms,
    terms,
    showModalTerms,
    setShowModalTerms,
    expiredWhileActionable,
    consentRequired,
    consentGranted,
    submitting,
    handleApprove,
    approvalBlockedByDeposit,
    approveAndPay,
  } = useQuoteActions();
  const ssBrand =
    resolvedDocLayout?.branding?.presentation === 'SINGLE_SCOPE'
      ? resolvedDocLayout.branding
      : null;
  const railCompany = printableSingleScopeCompany(documentCompany, resolvedDocLayout);
  const visibleEstimateNumber =
    documentNumber ?? (v.estimateNumber ? estimate.estimateNumber : null);
  const displayedOptionalRows = selectedOptionalRows.map((row) => {
    if (!acceptMembershipOffer) return row;
    const adjustment = membershipOfferAdjustmentForLines(
      membershipOffer,
      row.lines,
      estimate.lineItems,
    );
    return { ...row, subtotal: adjustment?.memberSubtotal ?? row.subtotal };
  });
  return (
    <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[540px]"
        style={singleScopeThemeVars(ssBrand)}
      >
        {/* .sheet__h — 18/20 pad, 12 gap, bottom hairline. */}
        <div className="flex shrink-0 items-center gap-3 border-b border-[#14141A]/[0.08] px-5 py-[18px]">
          <DialogTitle className="min-w-0 flex-1 text-[17px] font-medium tracking-[-0.02em] text-[#17171A]">
            Approve {documentLabel.toLowerCase()}
            {visibleEstimateNumber ? ` ${visibleEstimateNumber}` : ''}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Review the {documentLabel.toLowerCase()} total, sign if required, and accept the terms
            to approve.
          </DialogDescription>
          {/* .iconbtn — 36px, r6, --line border, muted. */}
          <button
            type="button"
            onClick={() => setApproveModalOpen(false)}
            aria-label="Close"
            className="flex size-9 shrink-0 items-center justify-center rounded-md border border-[#14141A]/[0.13] text-[#63646B] transition-colors hover:border-[#14141A]/[0.30] hover:text-[#17171A]"
          >
            <X className="size-[17px]" strokeWidth={1.8} />
          </button>
        </div>

        {/* .sheet__b — 20 pad, scrolls. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {/* .totals — bone-2 bg, --line-2 border, r-lg, 19/21 pad. */}
          <div className="rounded-[10px] border border-[#14141A]/[0.08] bg-[#FBFBFA] px-[21px] py-[19px]">
            {/* .kv rows: 8px vertical, 14.5px, k muted / v ink-500, --line-3 divider between. */}
            <div className="flex justify-between gap-4 py-2 text-[14.5px]">
              <span className="min-w-0 text-[#63646B]">
                {`Scope of work${estimate.title ? ` — ${estimate.title}` : ''}`}
              </span>
              <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                {fmt(requiredSubtotal)}
              </span>
            </div>
            {displayedOptionalRows.map((row, i) => (
              <div
                key={`modal-addon-${i}`}
                className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]"
              >
                <span className="min-w-0 text-[#63646B]">{row.name}</span>
                <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                  {fmt(row.subtotal)}
                </span>
              </div>
            ))}
            {/* Discount rows — so the visible lines reconcile to the Total the
                customer approves (the gross subtotal + tax would otherwise
                overstate a member-priced or discounted quote). */}
            {displayMembershipSavings > 0 && (
              <div className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]">
                <span className="min-w-0 text-emerald-600">
                  Membership savings
                  {membershipSavingsPlanNames ? ` (${membershipSavingsPlanNames})` : ''}
                </span>
                <span className="shrink-0 font-medium tabular-nums text-emerald-600">
                  -{fmt(displayMembershipSavings)}
                </span>
              </div>
            )}
            {membershipPlanPrice > 0 && (
              <div className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]">
                <span className="min-w-0 text-[#63646B]">
                  {membershipOffer?.planName ?? 'Membership'}
                </span>
                <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                  {fmt(membershipPlanPrice)}
                </span>
              </div>
            )}
            {membershipSetupFee > 0 && (
              <div className="flex justify-between gap-4 py-2 text-sm">
                <span>One-time setup fee</span>
                <span>{fmt(membershipSetupFee)}</span>
              </div>
            )}
            {selectedDiscountTotal > 0 && (
              <div className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]">
                <span className="min-w-0 text-[#63646B]">{estimateDiscountLabel}</span>
                <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                  -{fmt(selectedDiscountTotal)}
                </span>
              </div>
            )}
            <div className="flex justify-between gap-4 border-t border-[#0A0A0A]/[0.045] py-2 text-[14.5px]">
              <span className="text-[#63646B]">Sales tax</span>
              <span className="shrink-0 font-medium tabular-nums text-[#17171A]">
                {fmt(approvalTaxTotal)}
              </span>
            </div>
            {/* .totals__t — 13 mt, 15 pt, --line border; span 15/500, b 29/500. */}
            <div className="mt-[13px] flex items-baseline justify-between gap-4 border-t border-[#14141A]/[0.13] pt-[15px]">
              <span className="text-[15px] font-medium text-[#17171A]">Total</span>
              <b className="text-[29px] font-medium leading-none tracking-[-0.032em] tabular-nums text-[#17171A]">
                {fmt(approvalGrandTotal)}
              </b>
            </div>
          </div>

          {/* No hardcoded "what approving does" blurb — its claims (nothing
              charged today, we'll reach out to schedule) aren't universally true.
              The payment schedule below states the real terms when there are any. */}

          {/* Payment plan + consent — silent unless the estimate carries a plan. */}
          <PaymentPlanSlot />

          {/* .sign block (mt-24): field label + framed pad with hint/baseline and
              an internal Clear + name footer. The name slot is an input (we don't
              pre-know the signer on a public link). */}
          {approvalMethod === 'SIGNATURE' && (
            <div className="mt-6">
              <span className="mb-1.5 block text-[13px] font-medium text-[#63646B]">
                Sign to approve
              </span>
              <div className="overflow-hidden rounded-[10px] border border-[#14141A]/[0.13] bg-white">
                <div className="relative h-[150px]">
                  <SignaturePad
                    ref={sigPadRef}
                    unstyled
                    height={150}
                    onBegin={() => setSigHasInk(true)}
                  />
                  <div className="pointer-events-none absolute inset-x-[26px] bottom-[38px] border-t border-[#14141A]/[0.13]" />
                  {!sigHasInk && (
                    <span className="pointer-events-none absolute bottom-[18px] left-[26px] text-[12.5px] text-[#95969D]">
                      Draw your signature here
                    </span>
                  )}
                </div>
                {/* .sig__f — bone-2 bg, top hairline, Clear + name. */}
                <div className="flex items-center gap-3 border-t border-[#14141A]/[0.08] bg-[#FBFBFA] px-[14px] py-2.5">
                  <button
                    type="button"
                    onClick={() => {
                      sigPadRef.current?.clear();
                      setSigHasInk(false);
                    }}
                    className="text-[13px] font-medium text-[var(--ss-accent-strong)] hover:underline"
                  >
                    Clear
                  </button>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={sigName}
                    onChange={(e) => setSigName(e.target.value)}
                    aria-label="Signature name"
                    className="min-w-0 flex-1 bg-transparent text-right text-[13px] text-[#63646B] outline-none placeholder:text-[#95969D]"
                  />
                </div>
              </div>
              <label className="mt-4 block">
                <span className="mb-1.5 block text-[13px] font-medium text-[#63646B]">
                  Note (optional)
                </span>
                <input
                  type="text"
                  placeholder="Add a note"
                  value={sigNote}
                  onChange={(e) => setSigNote(e.target.value)}
                  className="h-10 w-full rounded-md border border-[#14141A]/[0.13] bg-white px-3 text-[14px] text-[#17171A] outline-none placeholder:text-[#95969D] focus:border-[#63646B]"
                />
              </label>
            </div>
          )}

          {/* .chk.mt-20 — authorization clause. "the terms" expands the estimate's
              terms inline (below) so they can be read without a modal-on-modal. */}
          <label className="mt-5 flex cursor-pointer items-start gap-[11px] text-[14.5px] leading-[1.45] text-[#374151]">
            <Checkbox
              checked={termsAccepted}
              onCheckedChange={(v) => setTermsAccepted(v === true)}
              className="mt-0.5"
            />
            <span>
              I authorize {railCompany?.name ?? 'the company'} to carry out the work described
              {visibleEstimateNumber ? ` in ${visibleEstimateNumber}` : ''}
              {hasTerms ? (
                <>
                  {' '}
                  and I accept{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      // Inside the <label>; don't let the click toggle the checkbox.
                      e.preventDefault();
                      e.stopPropagation();
                      setShowModalTerms((s) => !s);
                    }}
                    className="font-medium text-[var(--ss-accent-strong)] underline underline-offset-2 hover:text-[#17171A]"
                  >
                    the terms
                  </button>{' '}
                  in this {documentLabel.toLowerCase()}.
                </>
              ) : (
                <> and I approve this {documentLabel.toLowerCase()}.</>
              )}
            </span>
          </label>

          {/* Inline terms — expanded from the clause above; no second modal. */}
          {hasTerms && showModalTerms && (
            <div className="mt-3 max-h-56 overflow-y-auto rounded-[10px] border border-[#14141A]/[0.08] bg-[#FBFBFA] px-4 py-3">
              <CustomerTermsBody
                terms={terms}
                className="text-[13px] leading-[1.6] text-[#63646B]"
              />
            </div>
          )}

          {expiredWhileActionable && (
            <p className="mt-4 flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
              <CalendarClock className="mt-0.5 size-4 shrink-0" />
              <span>
                {expiredQuoteMessage({
                  validUntil: estimate.validUntil,
                  showValidUntil: v.validUntil,
                  formatDate: fmtDateOnly,
                })}
              </span>
            </p>
          )}
          {consentRequired && !consentGranted && (
            <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Authorize automatic payments above to continue.
            </p>
          )}
        </div>

        {/* .sheet__f — bone-2 bg, top hairline, 15/20 pad, equal buttons. */}
        <div className="flex shrink-0 gap-2.5 border-t border-[#14141A]/[0.08] bg-[#FBFBFA] px-5 py-[15px]">
          <Button
            variant="outline"
            className="h-11 flex-1 text-[15px] font-medium"
            onClick={() => setApproveModalOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            className={
              presentation === 'SINGLE_SCOPE'
                ? 'h-11 flex-1 bg-[var(--ss-accent)] text-[15px] font-medium text-white hover:bg-[var(--ss-accent-strong)]'
                : 'h-11 flex-1 bg-black text-[15px] font-medium text-white hover:bg-black/90'
            }
            onClick={handleApprove}
            disabled={
              submitting ||
              expiredWhileActionable ||
              approvalBlockedByDeposit ||
              !termsAccepted ||
              (consentRequired && !consentGranted)
            }
          >
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {approveAndPay ? 'Approve and pay' : `Approve ${fmt(approvalGrandTotal)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
